/**
 * ユーザーコンテキスト
 *
 * ユーザーデータ（プラン、使用量など）を管理
 */

import { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';

// ユーザーコンテキストを作成
const UserContext = createContext(null);

// プランの制限値
const PLAN_LIMITS = {
  free: {
    maxPages: 500,
    monthlyQueries: 5,
    hasAds: true
  },
  pro: {
    maxPages: 3000,
    monthlyQueries: 20,
    hasAds: false
  },
  // 後方互換性のため premium も pro と同じ扱い
  premium: {
    maxPages: 3000,
    monthlyQueries: 20,
    hasAds: false
  }
};

/**
 * ユーザープロバイダーコンポーネント
 */
export function UserProvider({ children }) {
  const { user } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ユーザーデータの変更を監視
  useEffect(() => {
    if (!user) {
      setUserData(null);
      setLoading(false);
      return;
    }

    console.log('[KnoVelo User] ユーザーデータの監視を開始');

    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setUserData({
          ...data,
          // プランの制限値を追加
          limits: PLAN_LIMITS[data.plan] || PLAN_LIMITS.free
        });
        console.log('[KnoVelo User] ユーザーデータを取得:', data.plan);
      } else {
        setUserData(null);
      }
      setLoading(false);
    }, (error) => {
      console.error('[KnoVelo User] ユーザーデータの取得エラー:', error);
      setLoading(false);
    });

    // クリーンアップ
    return () => unsubscribe();
  }, [user]);

  /**
   * ユーザーがProプラン（有料プラン）かどうか
   * 後方互換性のため premium も pro と同じ扱い
   */
  const isPremium = userData?.plan === 'pro' || userData?.plan === 'premium';

  /**
   * 広告を表示すべきかどうか
   */
  const shouldShowAds = !isPremium;

  /**
   * AI検索の残り回数
   */
  const remainingQueries = userData
    ? Math.max(0, (userData.limits?.monthlyQueries || 0) - (userData.monthlyQueryCount || 0))
    : 0;

  /**
   * AI検索の制限に達しているかどうか
   */
  const hasReachedQueryLimit = remainingQueries <= 0;

  /**
   * ページ保存の残り枠
   */
  const remainingPages = userData
    ? Math.max(0, (userData.limits?.maxPages || 0) - (userData.storageUsed || 0))
    : 0;

  /**
   * ページ保存の制限に達しているかどうか
   */
  const hasReachedPageLimit = remainingPages <= 0;

  const value = {
    userData,
    loading,
    isPremium,
    shouldShowAds,
    remainingQueries,
    hasReachedQueryLimit,
    remainingPages,
    hasReachedPageLimit,
    planLimits: PLAN_LIMITS
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

/**
 * ユーザーコンテキストを使用するカスタムフック
 */
export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserはUserProvider内で使用する必要があります');
  }
  return context;
}

export default UserContext;
